<template>
    <!-- MY1-3083: an existing user whose card is bank-connected but who has
         My Spending off — the one thing missing before auto-tracking can run.
         Opt in here, in place (the wallet's MY1-3077 pattern); on another
         member's card show why the benefit isn't tracked instead of an action
         that would opt the viewer in and leave the card untouched. -->
    <div
        v-if="showPrompt"
        class="mb-6 rounded-lg bg-slate-50 dark:bg-slate-700/50 px-4 py-3 flex flex-col gap-3"
        data-test="spending-prompt"
    >
        <div class="flex items-start gap-2">
            <Icon
                icon="calendar-check"
                class="w-5 h-5 shrink-0 text-slate-800 dark:text-slate-100"
            />
            <div class="flex flex-col gap-0.5 min-w-0">
                <p
                    class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100"
                >
                    Turn on My Spending
                </p>
                <p
                    class="text-sm font-normal leading-5 text-slate-600 dark:text-slate-300"
                >
                    Enable My Spending to track benefit usage automatically.
                </p>
            </div>
        </div>
        <div class="flex items-center gap-4">
            <button
                type="button"
                class="px-4 py-2 rounded-[4px] border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150 text-sm font-medium text-slate-800 dark:text-slate-100 disabled:opacity-60 disabled:cursor-not-allowed"
                :disabled="store.spendingOptInBusy"
                data-test="enable-spending"
                @click="enable"
            >
                Turn on My Spending
            </button>
            <button
                type="button"
                class="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors"
                data-test="dismiss-prompt"
                @click="dismiss"
            >
                Dismiss
            </button>
        </div>
    </div>

    <div
        v-else-if="showNote"
        class="mb-6 rounded-lg bg-slate-50 dark:bg-slate-700/50 px-4 py-3 flex items-start gap-2"
        data-test="spending-note"
    >
        <Icon
            icon="calendar-check"
            class="w-5 h-5 shrink-0 text-slate-500 dark:text-slate-400"
        />
        <p
            class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
        >
            Not tracked. {{ ownerLabel }} has My Spending turned off.
        </p>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import { useCardBenefitsStore } from '@/store/card-benefits';

const props = defineProps({
    // The benefit payload's `auto_tracking` object.
    autoTracking: {
        type: Object,
        default: null,
    },
    // The viewer owns the card, so the in-place opt-in may be offered.
    canEnableSpending: {
        type: Boolean,
        default: false,
    },
    ownerName: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['enable-spending', 'dismiss']);

const store = useCardBenefitsStore();

const spendingDisabled = computed(
    () => props.autoTracking?.state === 'spending_disabled'
);

const showPrompt = computed(
    () =>
        spendingDisabled.value &&
        props.canEnableSpending &&
        !store.spendingPromptDismissed
);

const showNote = computed(
    () => spendingDisabled.value && !props.canEnableSpending
);

const ownerLabel = computed(() => props.ownerName || "This card's owner");

function enable() {
    emit('enable-spending');
    store.enableSpending();
}

function dismiss() {
    emit('dismiss');
    store.dismissSpendingPrompt();
}
</script>
